'use client';

import { useEffect } from 'react';

import { ampli } from '@/ampli';

import { resolveAmplitudeApiKey, type DeploymentEnvironment } from './amplitude-environment';
import { buildIdentity, type TrackingViewer } from './amplitude-identity';

let ampliLoaded = false;

function loadAmpliOnce(env: DeploymentEnvironment) {
    if (ampliLoaded) return;
    if (typeof window === 'undefined') return;

    // Clé explicite plutôt que `environment` : le type `Environment` généré par
    // Ampli ne couvre pas le projet catalogue. Cf. `amplitude-environment.ts`.
    const apiKey = resolveAmplitudeApiKey(env);
    if (!apiKey) return;

    ampliLoaded = true;

    try {
        // Le `try/catch` ne couvre que le synchrone : `load()` renvoie une
        // promesse qui peut rejeter de façon asynchrone. Sans ce `.catch`,
        // un rejet remonterait en unhandled rejection — ce que le tracking ne
        // doit jamais produire.
        ampli
            .load({
                client: {
                    apiKey,
                    configuration: {
                        // `autocapture` remplace `defaultTracking`, déprécié en 2.x.
                        // `sessions` reste actif (défaut du SDK) : il conditionne
                        // toutes les métriques de session et ne capture aucune
                        // donnée saisie. `attribution` et `pageUrlEnrichment` sont
                        // aussi actifs par défaut et déclarés explicitement à `true` :
                        // ce sont de l'attribution de campagne (UTM) et de
                        // l'enrichissement d'URL, utiles sur un site public, sans
                        // capture de contenu saisi. Tout le reste est coupé
                        // explicitement — `elementInteractions` et
                        // `formInteractions` embarquent le texte et les sélecteurs
                        // des éléments, donc le contenu des champs du brief, de la
                        // recherche et des coordonnées client ; `frustrationInteractions`
                        // (rage/dead clicks) capture les mêmes sélecteurs et textes
                        // d'éléments qu'`elementInteractions`, donc la même fuite.
                        // Rien n'est laissé implicite : le prochain lecteur doit voir
                        // la décision pour chaque option, y compris si le SDK change
                        // un défaut.
                        autocapture: {
                            attribution: true,
                            pageUrlEnrichment: true,
                            pageViews: true,
                            sessions: true,
                            elementInteractions: false,
                            formInteractions: false,
                            frustrationInteractions: false,
                            fileDownloads: false,
                            networkTracking: false,
                            webVitals: false,
                        },
                    },
                },
            })
            ?.promise?.catch(() => {});
    } catch (error) {
        console.error('Amplitude load failed', error);
    }
}

type AmplitudeTrackingProps = {
    env: DeploymentEnvironment;
    viewer: TrackingViewer;
};

export function AmplitudeTracking({ env, viewer }: AmplitudeTrackingProps) {
    // Initialisation pendant le render, pas dans un `useEffect` : Ampli n'a pas
    // de file d'attente pré-init, donc tout event émis avant `load()` est perdu.
    // Le premier « Page Viewed » automatique part anonyme et se rattache à
    // l'utilisateur via le `device_id` quand `identify()` arrive.
    loadAmpliOnce(env);

    const email = viewer.role === 'visitor' ? undefined : viewer.email;

    useEffect(() => {
        if (!ampli.isLoaded) return;

        const identity = buildIdentity(viewer, window.innerWidth);
        if (!identity) return;

        try {
            // Même raison que pour `load()` : `identify()` renvoie une promesse,
            // et son rejet ne doit jamais devenir une unhandled rejection.
            ampli.identify(identity.userId, identity.properties)?.promise?.catch(() => {});
        } catch (error) {
            console.error('Amplitude identify failed', error);
        }
        // La session est stable sur la durée de vie de la page : un seul
        // identify, déclenché par le seul champ qui puisse changer d'identité.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one identify per identity change, see the note above
    }, [email]);

    return null;
}
